import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaMedal, FaSave, FaTimes, FaTrophy } from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import {
  emptyPalmaresForm,
  mapPalmaresToForm,
} from "../utils/palmaresUtils";

export default function PalmaresFormDrawer({
  isOpen,
  mode = "create",
  palmares,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyPalmaresForm);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && palmares) {
      setFormData(mapPalmaresToForm(palmares));
    } else {
      setFormData(emptyPalmaresForm);
    }
  }, [isOpen, isEdit, palmares]);

  const title = isEdit ? "Modifier un palmarès" : "Créer un palmarès";

  const subtitle = isEdit
    ? "Mettez à jour le titre, les résultats, la date et la visibilité."
    : "Ajoutez un nouveau résultat sportif à l’association sélectionnée.";

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.titre.trim()) {
      toast.error("Le titre du palmarès est obligatoire.");
      return;
    }

    if (!formData.date_palmares) {
      toast.error("La date du palmarès est obligatoire.");
      return;
    }

    if (isEdit) {
      await onUpdate?.(palmares.id, formData, palmares);
    } else {
      await onCreate?.(formData);
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <FormBlock
            icon={<FaTrophy />}
            title="Informations du palmarès"
            subtitle="Titre, résultats et statut de visibilité sur le site public."
          >
            <div className="grid gap-4">
              <InputField
                label="Titre du palmarès"
                name="titre"
                value={formData.titre}
                onChange={handleChange}
                required
                placeholder="Ex : Championnat départemental, Coupe régionale..."
              />

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Résultats
                </label>

                <textarea
                  name="resultats"
                  value={formData.resultats}
                  onChange={handleChange}
                  rows={8}
                  placeholder="Ex : 1er place, podiums, participants, catégories..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={Boolean(formData.is_active)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />

                <span className="inline-flex items-center gap-2">
                  <FaCheckCircle
                    className={
                      formData.is_active ? "text-emerald-500" : "text-slate-400"
                    }
                  />
                  Palmarès actif et visible
                </span>
              </label>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaMedal />}
            title="Date du résultat"
            subtitle="La date permet de trier les palmarès du plus récent au plus ancien."
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <InputField
                label="Date du palmarès"
                name="date_palmares"
                type="date"
                value={formData.date_palmares}
                onChange={handleChange}
                required
              />

              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                Conseil : utilisez la date réelle de la compétition ou de
                l’événement pour conserver un affichage chronologique propre.
              </div>
            </div>
          </FormBlock>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">
            {title}
          </h3>

          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}